import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trophy, Plus, Trash2, Save, Loader2, FlaskConical, ListChecks, History, CheckCircle2, ShieldAlert, XCircle, Activity, RefreshCw, AlertCircle, Wallet } from "lucide-react";
import { toast } from "sonner";
import StarPerformer from "@/components/StarPerformer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (n === null || n === undefined ? "—" : `R ${Number(n).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function CommissionEngine() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    try {
      const res = await axios.get(`${API}/commission/config`);
      // Back-fill `filters` block so older configs from before the
      // Feb-2026 filter feature still render cleanly in the UI.
      const safeCfg = {
        ...res.data,
        filters: res.data.filters || { excluded_categories: [], min_markup_pct: 0 },
      };
      setCfg(safeCfg);
    } catch (e) { toast.error("Failed to load config"); }
  })(); }, []);

  // Fetch product categories once for the Filters multi-select
  const [productCategories, setProductCategories] = useState([]);
  useEffect(() => { (async () => {
    try {
      const res = await axios.get(`${API}/commission/categories`);
      setProductCategories(res.data.categories || []);
    } catch (e) { /* non-fatal */ }
  })(); }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/commission/config`, cfg);
      toast.success("Config saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="p-6 flex items-center gap-2 text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading commission config…</div>;

  return (
    <div className="p-6 pb-24 space-y-6" data-testid="commission-page">
      <div className="flex items-center gap-3">
        <Trophy className="h-7 w-7 text-amber-500" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Commission & Bonus Engine</h1>
          <p className="text-sm text-gray-500">Reward staff for the months where the business actually makes money.</p>
        </div>
      </div>

      <Tabs defaultValue="live" className="w-full">
        <TabsList>
          <TabsTrigger value="live" data-testid="tab-live"><Activity className="h-4 w-4 mr-1" /> Live This Month</TabsTrigger>
          <TabsTrigger value="earnings" data-testid="tab-earnings"><Wallet className="h-4 w-4 mr-1" /> Live Earnings (per staff)</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config"><ListChecks className="h-4 w-4 mr-1" /> Configuration</TabsTrigger>
          <TabsTrigger value="dryrun" data-testid="tab-dryrun"><FlaskConical className="h-4 w-4 mr-1" /> Dry-Run</TabsTrigger>
          <TabsTrigger value="preview" data-testid="tab-preview"><CheckCircle2 className="h-4 w-4 mr-1" /> Single-Month Preview</TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-runs"><History className="h-4 w-4 mr-1" /> Approved Runs</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard"><Trophy className="h-4 w-4 mr-1" /> Leaderboard</TabsTrigger>
        </TabsList>

        {/* ============== LIVE THIS MONTH TAB ============== */}
        <TabsContent value="live"><LiveMonthPanel /></TabsContent>
        <TabsContent value="earnings"><LivePerEmployeePanel /></TabsContent>

        {/* ============== CONFIG TAB ============== */}
        <TabsContent value="config" className="space-y-4">
          {/* GUARDRAILS */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-red-600" /> Guardrails (the rules that protect the business)</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between gap-3 p-3 border rounded">
                <div>
                  <Label>Block all payouts during loss months</Label>
                  <p className="text-xs text-gray-500">If Net Profit ≤ 0, no bonuses at all.</p>
                </div>
                <Switch
                  checked={cfg.guardrails.require_positive_net_profit}
                  onCheckedChange={(v) => setCfg({ ...cfg, guardrails: { ...cfg.guardrails, require_positive_net_profit: v } })}
                  data-testid="switch-positive-np"
                />
              </div>
              <div>
                <Label>Max total payout as % of Net Profit</Label>
                <Input type="number" step="0.5" value={cfg.guardrails.max_pct_of_net_profit}
                  onChange={(e) => setCfg({ ...cfg, guardrails: { ...cfg.guardrails, max_pct_of_net_profit: Number(e.target.value) } })} />
                <p className="text-xs text-gray-500 mt-1">If raw payout exceeds this, all bonuses are scaled down proportionally.</p>
              </div>
            </CardContent>
          </Card>

          {/* GP FILTERS (excluded categories + min markup %) */}
          <Card data-testid="filters-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-indigo-600" />
                GP Filters — which sales count toward commission
              </CardTitle>
              <p className="text-xs text-gray-500 mt-1">
                These filters strip selected lines out of the GP used by <strong>Layer 1</strong> and <strong>Layer 2</strong>.
                Layer 3 behaviour bonuses, Net-Profit guardrails and the canonical Income Statement are unaffected.
              </p>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              {/* Excluded categories */}
              <div className="space-y-2">
                <Label>Exclude these stock categories</Label>
                <div className="border rounded p-2 max-h-48 overflow-y-auto space-y-1 bg-white">
                  {productCategories.length === 0 && (
                    <p className="text-xs text-gray-400 italic p-2">Loading categories…</p>
                  )}
                  {productCategories.map((cat) => {
                    const checked = (cfg.filters?.excluded_categories || []).includes(cat);
                    return (
                      <label
                        key={cat}
                        className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer text-sm"
                        data-testid={`filter-cat-${cat}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const cur = new Set(cfg.filters?.excluded_categories || []);
                            if (e.target.checked) cur.add(cat); else cur.delete(cat);
                            setCfg({ ...cfg, filters: { ...cfg.filters, excluded_categories: [...cur] } });
                          }}
                          className="h-4 w-4 text-indigo-600 rounded"
                        />
                        <span>{cat}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500">
                  Sales of products in these categories are <strong>removed</strong> from the GP pool used for bonuses.
                  Useful for low-margin staples (e.g. bread, milk) that you don't want to reward staff on.
                </p>
              </div>

              {/* Minimum markup % */}
              <div className="space-y-2">
                <Label>Minimum markup % to qualify</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={cfg.filters?.min_markup_pct ?? 0}
                    onChange={(e) => setCfg({ ...cfg, filters: { ...cfg.filters, min_markup_pct: Number(e.target.value) } })}
                    className="w-32"
                    data-testid="filter-min-markup"
                  />
                  <span className="text-sm text-gray-500">% markup</span>
                </div>
                <p className="text-xs text-gray-500">
                  Any line sold with markup below this % is excluded from the commission GP.
                  Markup % = <code className="bg-gray-100 px-1 rounded">(sell-price excl VAT − cost) ÷ cost × 100</code>.
                  Set to <strong>0</strong> to disable this filter. <br />
                  <span className="text-amber-700">Tip:</span> if staff are discounting heavily to push volume, raise this to (say) 15–20 % so deeply discounted sales don't pad their bonus.
                </p>
                <div className="text-xs bg-indigo-50 border border-indigo-200 rounded p-2 text-indigo-800">
                  Active filters summary:
                  <strong className="ml-1">
                    {(cfg.filters?.excluded_categories || []).length} categories excluded
                  </strong>,
                  <strong className="ml-1">
                    {Number(cfg.filters?.min_markup_pct || 0) > 0 ? `≥ ${cfg.filters.min_markup_pct}% markup required` : "no markup floor"}
                  </strong>.
                </div>
              </div>
            </CardContent>
          </Card>

          {/* LAYER 1 */}
          <Card>
            <CardHeader className="pb-2 flex flex-row justify-between items-center">
              <CardTitle className="text-base">Layer 1 — Store-wide threshold bonus</CardTitle>
              <Switch checked={cfg.layer1.enabled} onCheckedChange={(v) => setCfg({ ...cfg, layer1: { ...cfg.layer1, enabled: v } })} />
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Monthly GP target (R)</Label>
                <Input type="number" step="500" value={cfg.layer1.monthly_gp_target}
                  onChange={(e) => setCfg({ ...cfg, layer1: { ...cfg.layer1, monthly_gp_target: Number(e.target.value) } })} />
              </div>
              <div>
                <Label>Flat bonus per active staff (R)</Label>
                <Input type="number" step="50" value={cfg.layer1.bonus_per_staff}
                  onChange={(e) => setCfg({ ...cfg, layer1: { ...cfg.layer1, bonus_per_staff: Number(e.target.value) } })} />
              </div>
              <p className="md:col-span-2 text-xs text-gray-500">Every active employee gets this flat amount in months where the store exceeds the GP target.</p>
            </CardContent>
          </Card>

          {/* LAYER 2 */}
          <Card>
            <CardHeader className="pb-2 flex flex-row justify-between items-center">
              <CardTitle className="text-base">Layer 2 — GP-share commission pool</CardTitle>
              <Switch checked={cfg.layer2.enabled} onCheckedChange={(v) => setCfg({ ...cfg, layer2: { ...cfg.layer2, enabled: v } })} />
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Monthly GP target (R)</Label>
                <Input type="number" step="500" value={cfg.layer2.monthly_gp_target}
                  onChange={(e) => setCfg({ ...cfg, layer2: { ...cfg.layer2, monthly_gp_target: Number(e.target.value) } })} />
              </div>
              <div>
                <Label>Share of excess GP %</Label>
                <Input type="number" step="0.5" value={cfg.layer2.share_pct}
                  onChange={(e) => setCfg({ ...cfg, layer2: { ...cfg.layer2, share_pct: Number(e.target.value) } })} />
              </div>
              <div className="md:col-span-2">
                <Label>Split method</Label>
                <select
                  className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm bg-white"
                  value={cfg.layer2.split_method || "equal"}
                  onChange={(e) => setCfg({ ...cfg, layer2: { ...cfg.layer2, split_method: e.target.value } })}
                  data-testid="layer2-split-method"
                >
                  <option value="equal">Split equally — every active staff gets the same share</option>
                  <option value="proportional_by_gp">Proportional by individual GP — each staff earns based on the GP they personally generated ⭐</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  <strong>Proportional</strong> is recommended — a staff member who sold 80% of the month's GP gets 80% of the pool. Staff who didn't ring up any sales get R 0 from this layer.
                </p>
              </div>
              <div>
                <Label>Max per staff (R, 0 = no cap)</Label>
                <Input type="number" step="100" value={cfg.layer2.max_per_staff}
                  onChange={(e) => setCfg({ ...cfg, layer2: { ...cfg.layer2, max_per_staff: Number(e.target.value) } })} />
              </div>
            </CardContent>
          </Card>

          {/* LAYER 3 */}
          <Card>
            <CardHeader className="pb-2 flex flex-row justify-between items-center">
              <CardTitle className="text-base">Layer 3 — Behaviour micro-bonuses (per logged event)</CardTitle>
              <Switch checked={cfg.layer3.enabled} onCheckedChange={(v) => setCfg({ ...cfg, layer3: { ...cfg.layer3, enabled: v } })} />
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">Enabled</TableHead>
                    <TableHead>Rule key</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead className="w-32 text-right">Amount (R)</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cfg.layer3.rules.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell><Switch checked={r.enabled !== false} onCheckedChange={(v) => updateRule(cfg, setCfg, i, "enabled", v)} /></TableCell>
                      <TableCell><Input value={r.key} onChange={(e) => updateRule(cfg, setCfg, i, "key", e.target.value)} className="h-8 font-mono text-xs" /></TableCell>
                      <TableCell><Input value={r.label} onChange={(e) => updateRule(cfg, setCfg, i, "label", e.target.value)} className="h-8" /></TableCell>
                      <TableCell className="text-right"><Input type="number" step="5" value={r.amount} onChange={(e) => updateRule(cfg, setCfg, i, "amount", Number(e.target.value))} className="h-8 text-right" /></TableCell>
                      <TableCell><Button variant="ghost" size="icon" onClick={() => removeRule(cfg, setCfg, i)}><Trash2 className="h-4 w-4 text-red-600" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => addRule(cfg, setCfg)} data-testid="add-rule"><Plus className="h-4 w-4 mr-1" /> Add rule</Button>
              <p className="text-xs text-gray-500 mt-2">Events are logged via <code>POST /api/commission/behaviour-events</code> (manager can also log them manually). On approval, paid events lock so they never double-pay.</p>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={saveConfig} disabled={saving} data-testid="btn-save-config">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save configuration
            </Button>
          </div>
        </TabsContent>

        {/* ============== DRY RUN TAB ============== */}
        <TabsContent value="dryrun"><DryRunPanel /></TabsContent>

        {/* ============== PREVIEW TAB ============== */}
        <TabsContent value="preview"><PreviewPanel /></TabsContent>

        {/* ============== RUNS TAB ============== */}
        <TabsContent value="runs"><RunsPanel /></TabsContent>

        {/* ============== LEADERBOARD TAB ============== */}
        <TabsContent value="leaderboard"><LeaderboardPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function updateRule(cfg, setCfg, idx, field, value) {
  const rules = [...cfg.layer3.rules];
  rules[idx] = { ...rules[idx], [field]: value };
  setCfg({ ...cfg, layer3: { ...cfg.layer3, rules } });
}
function addRule(cfg, setCfg) {
  setCfg({ ...cfg, layer3: { ...cfg.layer3, rules: [...cfg.layer3.rules, { key: "new_rule", label: "", amount: 0, enabled: true }] } });
}
function removeRule(cfg, setCfg, idx) {
  setCfg({ ...cfg, layer3: { ...cfg.layer3, rules: cfg.layer3.rules.filter((_, i) => i !== idx) } });
}

/* ============== Live This Month Panel ==============
 * Auto-loads the current SAST year/month payout preview, refreshes every
 * 60 s, exposes a manual refresh button + last-refresh timestamp.
 * Read-only — no "Create draft" / "Approve" actions (those live on the
 * Single-Month Preview tab where the user explicitly picks a month). */
function LiveMonthPanel() {
  // SAST = UTC+2. Use it directly so the month boundary matches the rest
  // of the financial stack (cashups, daily summary, income statement).
  const sastNow = () => {
    const now = new Date();
    return new Date(now.getTime() + (2 * 60 - now.getTimezoneOffset()) * 60000);
  };
  const [{ year, month }, setPeriod] = useState(() => {
    const d = sastNow();
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
  });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [error, setError] = useState(null);

  const load = async ({ silent } = {}) => {
    if (!silent) setBusy(true);
    try {
      const d = sastNow();
      const y = d.getUTCFullYear();
      const m = d.getUTCMonth() + 1;
      setPeriod({ year: y, month: m });
      const res = await axios.get(`${API}/commission/preview?year=${y}&month=${m}`);
      setData(res.data);
      setLastRefreshed(new Date());
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      if (!silent) setBusy(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load({ silent: true }), 60000); // 60 s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmtTime = (d) => d ? d.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";
  const sortedPayouts = useMemo(() => {
    if (!data?.payouts) return [];
    return [...data.payouts].sort((a, b) => (b.total || 0) - (a.total || 0));
  }, [data]);

  return (
    <Card data-testid="live-month-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4 text-emerald-600" />
              Live month-to-date payout · <span className="text-emerald-700">{MONTHS[month]} {year}</span>
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              Real-time preview of what each employee would be paid if the month closed right now.
              Refreshes automatically every 60 seconds.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-mono text-[11px]" data-testid="live-last-refreshed">
              Updated {fmtTime(lastRefreshed)}
            </Badge>
            <Button size="sm" variant="outline" onClick={() => load()} disabled={busy} data-testid="btn-live-refresh">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {error && (
          <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            <XCircle className="h-4 w-4" /> {error}
          </div>
        )}

        {!data && busy && (
          <div className="flex items-center gap-2 text-gray-500 p-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading live payout…
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Gross Profit (MTD)" value={fmt(data.metrics.gross_profit)} />
              <Stat
                label="Net Profit (MTD)"
                value={fmt(data.metrics.net_profit)}
                valueClass={data.metrics.net_profit < 0 ? "text-red-700" : "text-green-700"}
              />
              <Stat label="Active staff" value={data.metrics.active_employees} />
              <Stat
                label={data.metrics.filters_applied ? "Commission-eligible GP" : "Raw total"}
                value={data.metrics.filters_applied ? fmt(data.metrics.commission_eligible_gp) : fmt(data.guardrail.raw_total)}
                valueClass={data.metrics.filters_applied ? "text-indigo-700" : ""}
              />
              <Stat
                label="Live payout pool"
                value={fmt(data.guardrail.final_total)}
                valueClass="text-amber-700 font-bold"
              />
            </div>

            {data.metrics.filters_applied && (
              <div className="flex items-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded text-indigo-800 text-xs">
                <FlaskConical className="h-4 w-4" />
                GP Filters active —
                {data.metrics.excluded_categories?.length > 0 && (
                  <span className="ml-1">excluded categories: <strong>{data.metrics.excluded_categories.join(", ")}</strong> ({fmt(data.metrics.excluded_by_category)}).</span>
                )}
                {data.metrics.min_markup_pct > 0 && (
                  <span className="ml-1">≥{data.metrics.min_markup_pct}% markup floor stripped {fmt(data.metrics.excluded_by_low_markup)}.</span>
                )}
              </div>
            )}

            {data.guardrail.blocked_by_loss && (
              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                <XCircle className="h-4 w-4" /> Loss-blocked so far this month — no payout will be made unless Net Profit turns positive by month-end.
              </div>
            )}
            {data.guardrail.capped && (
              <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm">
                <ShieldAlert className="h-4 w-4" /> Raw payout was capped at {fmt(data.guardrail.max_allowed)} (max % of Net Profit guardrail).
              </div>
            )}

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-right">Layer 1<br/><span className="text-[10px] font-normal text-gray-400">Store target</span></TableHead>
                  <TableHead className="text-right">Layer 2<br/><span className="text-[10px] font-normal text-gray-400">Individual target</span></TableHead>
                  <TableHead className="text-right">Layer 3<br/><span className="text-[10px] font-normal text-gray-400">Behaviour</span></TableHead>
                  <TableHead className="text-right">Live Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedPayouts.map((p, idx) => (
                  <TableRow key={p.employee_id} data-testid={`live-payout-row-${idx}`} className={idx === 0 && p.total > 0 ? "bg-amber-50/60" : ""}>
                    <TableCell className="font-medium">
                      {idx === 0 && p.total > 0 && <Trophy className="h-3.5 w-3.5 text-amber-500 inline mr-1" />}
                      {p.name}
                    </TableCell>
                    <TableCell className="text-right">{fmt(p.layer1)}</TableCell>
                    <TableCell className="text-right">{fmt(p.layer2)}</TableCell>
                    <TableCell className="text-right">{fmt(p.layer3)}</TableCell>
                    <TableCell className="text-right font-semibold text-amber-700">{fmt(p.total)}</TableCell>
                  </TableRow>
                ))}
                {!sortedPayouts.length && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-sm text-gray-500 py-6">
                      No payouts so far this month.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <p className="text-[11px] text-gray-400 italic">
              Note: live figures change as sales come in and behaviour events are logged.
              Numbers freeze only once you approve the run on the Single-Month Preview tab.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* ============== Dry Run Panel ============== */
function DryRunPanel() {
  const [months, setMonths] = useState(6);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const fetchData = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/commission/dry-run?months=${Number(months) || 6}`);
      setData(res.data);
    } catch (e) { toast.error("Dry-run failed"); }
    finally { setBusy(false); }
  };
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Trailing dry-run — what would the engine have paid?</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-3">
          <div>
            <Label>Number of months back</Label>
            <Input type="number" min="1" max="36" value={months} onChange={(e) => setMonths(e.target.value)} className="w-32" data-testid="dryrun-months" />
          </div>
          <Button onClick={fetchData} disabled={busy} data-testid="btn-dryrun">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FlaskConical className="h-4 w-4 mr-1" />} Run</Button>
        </div>
        {data && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Total would-have-paid" value={fmt(data.summary.total_would_have_paid)} />
              <Stat label="Months with a payout" value={data.summary.months_with_payout} />
              <Stat label="Months blocked by loss" value={data.summary.months_blocked_by_loss} valueClass="text-red-700" />
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">GP</TableHead>
                  <TableHead className="text-right">Net Profit</TableHead>
                  <TableHead>Layers active</TableHead>
                  <TableHead className="text-right">Payout</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.results.map((r) => (
                  <TableRow key={`${r.year}-${r.month}`}>
                    <TableCell className="font-medium">{MONTHS[r.month]} {r.year}</TableCell>
                    <TableCell className="text-right">{fmt(r.metrics.gross_profit)}</TableCell>
                    <TableCell className={`text-right ${r.metrics.net_profit < 0 ? "text-red-700" : ""}`}>{fmt(r.metrics.net_profit)}</TableCell>
                    <TableCell className="text-xs">
                      {["layer1", "layer2", "layer3"].map((k) => r.layers[k].active && <Badge key={k} variant="outline" className="mr-1">{k}</Badge>)}
                    </TableCell>
                    <TableCell className="text-right font-semibold">{fmt(r.guardrail.final_total)}</TableCell>
                    <TableCell>{r.guardrail.blocked_by_loss && <Badge variant="destructive">Loss-blocked</Badge>}{r.guardrail.capped && <Badge className="bg-amber-100 text-amber-800 ml-1">Capped</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* ============== Single-Month Preview + Approve ============== */
function PreviewPanel() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approver, setApprover] = useState("");
  const [approvalNote, setApprovalNote] = useState("");
  const [runId, setRunId] = useState(null);
  const [approving, setApproving] = useState(false);

  const fetchPreview = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/commission/preview?year=${year}&month=${month}`);
      setData(res.data);
      setRunId(null);
    } catch (e) { toast.error("Preview failed"); }
    finally { setBusy(false); }
  };

  const createDraft = async () => {
    try {
      const res = await axios.post(`${API}/commission/runs`, { year: Number(year), month: Number(month) });
      setRunId(res.data.id);
      toast.success("Draft run created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create run"); }
  };

  const approveRun = async () => {
    if (!runId || !approver) return;
    setApproving(true);
    try {
      await axios.post(`${API}/commission/runs/${runId}/approve`, { approver, note: approvalNote });
      toast.success("Run approved & locked");
      setApproveOpen(false);
      setApprover(""); setApprovalNote("");
      setRunId(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Approval failed"); }
    finally { setApproving(false); }
  };

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Compute & approve a single month</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label>Year</Label><Input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="w-28" /></div>
          <div><Label>Month</Label><Input type="number" min="1" max="12" value={month} onChange={(e) => setMonth(e.target.value)} className="w-20" /></div>
          <Button onClick={fetchPreview} disabled={busy} data-testid="btn-preview">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Compute</Button>
          {data && !data.guardrail.blocked_by_loss && data.guardrail.final_total > 0 && !runId && (
            <Button variant="outline" onClick={createDraft} data-testid="btn-create-draft">Create draft run</Button>
          )}
          {runId && (
            <Button onClick={() => setApproveOpen(true)} className="bg-green-600 hover:bg-green-700" data-testid="btn-approve">
              <CheckCircle2 className="h-4 w-4 mr-1" /> Approve & lock
            </Button>
          )}
        </div>

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Gross Profit" value={fmt(data.metrics.gross_profit)} />
              <Stat label="Net Profit" value={fmt(data.metrics.net_profit)} valueClass={data.metrics.net_profit < 0 ? "text-red-700" : "text-green-700"} />
              <Stat label="Active staff" value={data.metrics.active_employees} />
              <Stat
                label={data.metrics.filters_applied ? "Commission-eligible GP" : "Raw total"}
                value={data.metrics.filters_applied ? fmt(data.metrics.commission_eligible_gp) : fmt(data.guardrail.raw_total)}
                valueClass={data.metrics.filters_applied ? "text-indigo-700" : ""}
              />
              <Stat label="Final payout" value={fmt(data.guardrail.final_total)} valueClass="text-amber-700" />
            </div>
            {data.metrics.filters_applied && (
              <div className="flex items-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded text-indigo-800 text-xs">
                <FlaskConical className="h-4 w-4" />
                GP Filters active —
                {data.metrics.excluded_categories?.length > 0 && (
                  <span className="ml-1">excluded categories: <strong>{data.metrics.excluded_categories.join(", ")}</strong> ({fmt(data.metrics.excluded_by_category)}).</span>
                )}
                {data.metrics.min_markup_pct > 0 && (
                  <span className="ml-1">≥{data.metrics.min_markup_pct}% markup floor stripped {fmt(data.metrics.excluded_by_low_markup)}.</span>
                )}
              </div>
            )}
            {data.guardrail.blocked_by_loss && (
              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                <XCircle className="h-4 w-4" /> Loss-blocked — no payout for this month
              </div>
            )}
            {data.guardrail.capped && (
              <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm">
                <ShieldAlert className="h-4 w-4" /> Raw payout was capped at {fmt(data.guardrail.max_allowed)} (max % of NP guardrail).
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-right">Layer 1</TableHead>
                  <TableHead className="text-right">Layer 2</TableHead>
                  <TableHead className="text-right">Layer 3</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.payouts.map((p) => (
                  <TableRow key={p.employee_id}>
                    <TableCell>{p.name}</TableCell>
                    <TableCell className="text-right">{fmt(p.layer1)}</TableCell>
                    <TableCell className="text-right">{fmt(p.layer2)}</TableCell>
                    <TableCell className="text-right">{fmt(p.layer3)}</TableCell>
                    <TableCell className="text-right font-semibold">{fmt(p.total)}</TableCell>
                  </TableRow>
                ))}
                {!data.payouts.length && <TableRow><TableCell colSpan={5} className="text-center text-sm text-gray-500 py-6">No payouts for this month.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>

      <AlertDialog open={approveOpen} onOpenChange={setApproveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve commission run for {MONTHS[month]} {year}?</AlertDialogTitle>
            <AlertDialogDescription>Approval locks the payout, marks behaviour events as paid, and writes the run to history. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label>Your name / signature</Label>
            <Input value={approver} onChange={(e) => setApprover(e.target.value)} placeholder="e.g. Herman" data-testid="input-approver" />
            <Label>Note (optional)</Label>
            <Input value={approvalNote} onChange={(e) => setApprovalNote(e.target.value)} placeholder="e.g. Paid on 28-Feb payslip" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={approving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={approveRun} disabled={approving || !approver}>
              {approving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

/* ============== Approved-Runs History ============== */
function RunsPanel() {
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/commission/runs?limit=200`);
      setRuns(res.data.runs || []);
    } catch (e) { toast.error("Failed to load runs"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between"><CardTitle className="text-base">Run history</CardTitle><Button size="sm" variant="outline" onClick={load} disabled={busy}>Refresh</Button></CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Period</TableHead><TableHead>Status</TableHead><TableHead>Approved by</TableHead><TableHead>Approved at</TableHead><TableHead className="text-right">Total</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {runs.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{MONTHS[r.month]} {r.year}</TableCell>
                <TableCell><Badge variant={r.status === "approved" ? "default" : "outline"}>{r.status}</Badge></TableCell>
                <TableCell>{r.approved_by || "—"}</TableCell>
                <TableCell className="text-xs">{r.approved_at ? new Date(r.approved_at).toLocaleString("en-ZA") : "—"}</TableCell>
                <TableCell className="text-right font-semibold">{fmt(r.total)}</TableCell>
              </TableRow>
            ))}
            {!runs.length && <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-6">No runs yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, valueClass = "" }) {
  return (
    <div className="p-3 border rounded bg-white">
      <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`text-xl font-semibold ${valueClass}`}>{value}</div>
    </div>
  );
}

/* ============== Leaderboard Panel (admin view) ============== */
function LeaderboardPanel() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("current_month");
  useEffect(() => { (async () => {
    try {
      const res = await axios.get(`${API}/commission/leaderboard?period=${period}&top=20`);
      setData(res.data);
    } catch (e) { toast.error("Failed to load leaderboard"); }
  })(); }, [period]);
  return (
    <div className="space-y-4">
      <StarPerformer />
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Full ranking — {data?.period_label || "—"}</CardTitle>
          <div className="flex gap-1">
            {[{k:"current_month",l:"This Month"},{k:"last_month",l:"Last Month"},{k:"last_30_days",l:"Last 30 Days"}].map(p => (
              <button key={p.k} onClick={() => setPeriod(p.k)}
                className={`text-xs px-2 py-1 rounded border ${period === p.k ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`}>
                {p.l}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead>Salesperson</TableHead>
              <TableHead>Marketing #</TableHead>
              <TableHead className="text-right">Gross Profit</TableHead>
              <TableHead className="text-right">Revenue (excl)</TableHead>
              <TableHead className="text-right">Sales</TableHead>
              <TableHead className="text-right">Items</TableHead>
              <TableHead className="text-right">⭐ Events</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(data?.all || []).map((r, i) => (
                <TableRow key={r.salesperson_id || r.salesperson || i}>
                  <TableCell className="font-semibold">{i + 1}</TableCell>
                  <TableCell className="font-medium">{r.salesperson}</TableCell>
                  <TableCell className="font-mono text-xs">{r.marketing_number || "—"}</TableCell>
                  <TableCell className={`text-right font-semibold ${r.gross_profit < 0 ? "text-red-700" : "text-green-700"}`}>{fmt(r.gross_profit)}</TableCell>
                  <TableCell className="text-right">{fmt(r.revenue_excl_vat)}</TableCell>
                  <TableCell className="text-right">{r.transactions}</TableCell>
                  <TableCell className="text-right">{r.items_sold}</TableCell>
                  <TableCell className="text-right">{r.behaviour_events}</TableCell>
                </TableRow>
              ))}
              {!data?.all?.length && <TableRow><TableCell colSpan={8} className="text-center text-gray-500 py-6">No salesperson sales recorded for this period.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}


// ============== LIVE PER-EMPLOYEE EARNINGS PANEL ==============
// Shows what each employee has earned in commission so far this month,
// regardless of whether the store has crossed into profit yet. Useful as
// a daily morale / "what am I sitting on" indicator. Approved payouts
// still respect the loss guardrail — this is projection-only.
function LivePerEmployeePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFetched, setLastFetched] = useState(null);
  // "What-if at current sales" mode — bypasses the Layer 1 / 2 target
  // gates so the user can see what each staff would earn from sales
  // already on the books, regardless of whether the store has hit the
  // GP target yet. Useful for validating that the configured rules are
  // realistic before committing to them.
  const [ignoreTargets, setIgnoreTargets] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/commission/live-per-employee`, {
        params: { ignore_targets: ignoreTargets ? "true" : "false" },
      });
      setData(res.data);
      setLastFetched(new Date());
    } catch (e) {
      toast.error("Could not load live earnings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Refresh every 2 minutes — cheap enough, fresh enough.
    const id = setInterval(fetchData, 120000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ignoreTargets]);

  if (!data) return <div className="text-sm text-gray-500 py-6 text-center"><Loader2 className="h-4 w-4 inline-block animate-spin mr-1" /> Loading live earnings…</div>;

  const fmt = (v) => `R ${Number(v || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const monthLabel = new Date(data.year, data.month - 1, 1).toLocaleString("en-ZA", { month: "long", year: "numeric" });
  const npNegative = (data.metrics?.net_profit ?? 0) <= 0;

  return (
    <div className="space-y-4">
      {/* Banner: are we currently in projection-only (loss) mode? */}
      {npNegative && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="py-3 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <strong>Projection only — not payable yet.</strong> The store is currently at a Net Profit of <strong>{fmt(data.metrics.net_profit)}</strong>.
              With the <em>"require positive net profit"</em> guardrail on, commission cannot pay out until the store turns profitable.
              The numbers below show what each employee <em>would</em> be due if the store closed today — they update as more sales come in.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top-line summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="py-3">
          <div className="text-xs text-gray-500">Period</div>
          <div className="text-lg font-semibold">{monthLabel}</div>
          <div className="text-[10px] text-gray-400 mt-1">MTD</div>
        </CardContent></Card>
        <Card><CardContent className="py-3">
          <div className="text-xs text-gray-500">Store GP (commission-eligible)</div>
          <div className="text-lg font-semibold text-emerald-700">{fmt(data.metrics.commission_eligible_gp)}</div>
        </CardContent></Card>
        <Card><CardContent className="py-3">
          <div className="text-xs text-gray-500">Store Net Profit</div>
          <div className={`text-lg font-semibold ${npNegative ? "text-red-700" : "text-emerald-700"}`}>{fmt(data.metrics.net_profit)}</div>
        </CardContent></Card>
        <Card><CardContent className="py-3">
          <div className="text-xs text-gray-500">Total earned (all staff)</div>
          <div className="text-lg font-semibold">{fmt(data.guardrail.final_total)}</div>
          <div className="text-[10px] text-gray-400 mt-1">{data.metrics.active_employees} active staff</div>
        </CardContent></Card>
      </div>

      {/* What-if toggle */}
      <Card className={ignoreTargets ? "border-purple-300 bg-purple-50/30" : ""}>
        <CardContent className="py-3 flex items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <FlaskConical className={`h-5 w-5 mt-0.5 ${ignoreTargets ? "text-purple-700" : "text-gray-400"}`} />
            <div>
              <div className="text-sm font-semibold">
                {ignoreTargets ? "What-if mode (targets ignored)" : "Strict mode (rules as configured)"}
              </div>
              <p className="text-xs text-gray-600 mt-0.5">
                {ignoreTargets
                  ? "Showing what each staffer would earn from current sales as if Layer 1 / 2 GP targets were already met. Use this to evaluate whether your rules are realistic before committing."
                  : "Showing exactly what each staffer would earn under your current rules — targets enforced. Toggle on to see the 'what-if' projection regardless of target gates."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Label htmlFor="ignore-targets" className="text-xs cursor-pointer">Ignore targets</Label>
            <Switch
              id="ignore-targets"
              checked={ignoreTargets}
              onCheckedChange={setIgnoreTargets}
              data-testid="ignore-targets-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Per-employee breakdown */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Per-employee earnings — running for {monthLabel}</CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              Refreshes every 2 minutes. Last update: {lastFetched ? lastFetched.toLocaleTimeString("en-ZA") : "—"}.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh now"}
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Employee</TableHead>
                <TableHead className="text-right">Layer 1<br /><span className="text-[10px] text-gray-400 font-normal">flat bonus</span></TableHead>
                <TableHead className="text-right">Layer 2<br /><span className="text-[10px] text-gray-400 font-normal">commission</span></TableHead>
                <TableHead className="text-right">Layer 3<br /><span className="text-[10px] text-gray-400 font-normal">behaviour</span></TableHead>
                <TableHead className="text-right font-bold">Earned so far</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data.payouts || []).map((p, i) => (
                <TableRow key={p.employee_id || i} className={p.total > 0 ? "" : "opacity-60"}>
                  <TableCell className="font-semibold">{i + 1}</TableCell>
                  <TableCell className="font-medium">
                    {p.name}
                    {p.layer2_gp_contribution !== undefined && p.layer2_gp_contribution > 0 && (
                      <div className="text-[10px] text-emerald-700 mt-0.5">
                        GP generated: {fmt(p.layer2_gp_contribution)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-gray-700">{fmt(p.layer1)}</TableCell>
                  <TableCell className="text-right text-gray-700">{fmt(p.layer2)}</TableCell>
                  <TableCell className="text-right text-gray-700">{fmt(p.layer3)}</TableCell>
                  <TableCell className="text-right font-bold text-emerald-700">{fmt(p.total)}</TableCell>
                </TableRow>
              ))}
              {!data.payouts?.length && (
                <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-6">No active employees configured.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Help footer */}
      <Card className="bg-gray-50">
        <CardContent className="py-3 text-xs text-gray-600 space-y-1">
          <div><strong>How this is calculated:</strong></div>
          {(() => {
            const cfg = data.config_snapshot || {};
            const l1 = cfg.layer1 || {};
            const l2 = cfg.layer2 || {};
            const l1r = data.layers?.layer1?.reason;
            const l2r = data.layers?.layer2?.reason;
            return (
              <>
                <div>
                  • <strong>Layer 1</strong> — R {(l1.bonus_per_staff || 0).toFixed(2)} flat bonus per active staff when store GP hits R {(l1.monthly_gp_target || 0).toFixed(2)}.
                  {l1r && <span className="text-amber-700 ml-1">⚠ {l1r}</span>}
                </div>
                <div>
                  • <strong>Layer 2</strong> — {(l2.share_pct || 0).toFixed(1)}% of GP above R {(l2.monthly_gp_target || 0).toFixed(2)}, split by <em>{l2.split_method === "proportional_by_gp" ? "individual GP contribution" : "equal share"}</em>.
                  {l2r && <span className="text-amber-700 ml-1">⚠ {l2r}</span>}
                </div>
                <div>• <strong>Layer 3</strong> — behaviour events logged this month at their configured rand value.</div>
                <div>• <strong>Live mode</strong> intentionally bypasses the loss guardrail so you can see what's accruing day by day. The actual payout (via the Approved Runs tab) still requires the store to be profitable.</div>
              </>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}
